import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogManagerService } from '../../../services/dialog-manager.service';
import { EmailService } from '../../../services/email.service';
import { EditorModule } from 'primeng/editor';
import { Email } from '../../../models/email.model';
import { AuthService } from '../../../services/auth.service';
import { ActivityService } from '../../../services/activity.service';
import { NotificationService } from '../../../services/notification.service';
import { CommonModule } from '@angular/common';
import { STATUS_OPTIONS } from '../../../../constants';
import { DropdownModule } from 'primeng/dropdown';
import { Activity } from '../../../models/activity.model';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { user } from '../../../models/user.model';
import { UserService } from '../../../services/user.service';
import { FloatLabelModule } from 'primeng/floatlabel';
import { CalendarModule } from 'primeng/calendar';
import { DateTimeService } from '../../../services/date-time.service';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DialogResult } from 'src/app/workflow/models/dialog-result.model';

@Component({
  selector: 'app-save-email',
  standalone: true,
  imports: [
    ButtonModule,
    FormsModule,
    ReactiveFormsModule,
    EditorModule,
    CommonModule,
    DropdownModule,
    FloatLabelModule,
    CalendarModule,
    InputTextModule,
    DialogModule,
    RadioButtonModule,
  ],
  templateUrl: './save-email.component.html',
  styleUrl: './save-email.component.scss',
})
export class SaveEmailComponent implements OnInit {
  private selectedUser: user | undefined;
  private activityToUpgrade: Activity | undefined;

  statusOptions = STATUS_OPTIONS;

  availableUsers: user[] = [];

  currentUserId: number | null;

  emailForm: FormGroup;
  isSubmitting = false;
  usersLoading = true;
  //#region Hilfe-Button

  displayHelpDialog: boolean = false;
  helpText: string = `Von: Max Mustermann <Max@wave-solutions.de>

Gesendet: Montag, 7. April 2025 11:13

An: Otto Rerner <Otto@wave-solutions.de>

Betreff: Beispiel 

sehr geehrter Max Mustermann,

die ist eine Beispiel email nur für die Hilfe zu gebrauchen.`;

  openHelpDialog() {
    this.displayHelpDialog = true;
  }
  //#endregion

  constructor(
    private fb: FormBuilder,
    private dialogManager: DialogManagerService,
    private dialogConfig: DynamicDialogConfig,
    private emailService: EmailService,
    private authService: AuthService,
    private activityService: ActivityService,
    private notificationService: NotificationService,
    private userService: UserService,
    private dateTimeService: DateTimeService
  ) {}

  ngOnInit(): void {
    this.activityToUpgrade = this.dialogConfig.data.activityToUpgrade;
    this.selectedUser = this.dialogConfig.data.user;
    this.currentUserId = this.authService.getCurrentUserId();

    this.initializeForm();
    this.loadUsers();
    this.setForm();

    if (!this.isUpdate && this.selectedUser?.userId) {
      this.emailForm.controls['participantId'].setValue(
        this.selectedUser.userId
      );
    }

    this.emailForm.controls['emailType'].valueChanges.subscribe((value) => {
      this.selectedEmailType = value;
    });
  }

  //#region HELPERS

  get isUpdate() {
    return this.activityToUpgrade != null;
  }

  get isUserSelected() {
    return this.selectedUser != null;
  }

  enableUserDropdown(): void {
    this.usersLoading = false;
    this.emailForm.controls['sender'].enable();
    this.emailForm.controls['recipient'].enable();
    this.emailForm.controls['participantId'].enable();
  }

  closeDialog(result?: DialogResult): void {
    this.dialogManager.closeEmailDialog(result);
  }

  //#endregion

  //#region INITIALIZING

  loadUsers(): void {
    this.usersLoading = true;
    this.userService.getUsersObservable().subscribe({
      next: (users) => {
        this.availableUsers = users.sort((a, b) =>
          a.lastname.localeCompare(b.lastname)
        );
        this.enableUserDropdown();
      },
      error: (err) => {
        console.error('Fehler beim Laden der Nutzer', err);
        this.enableUserDropdown();
      },
    });
  }

  initializeForm(): void {
    this.emailForm = this.fb.group({
      text: ['', Validators.required],
      status: ['NEU ERSTELLT'],
      subject: [''],
      sender: [null],
      recipient: [null],
      sentAt: [''],
      participantId: ['', Validators.required],
      emailType: [15],
    });

    this.emailForm.controls['sender'].disable();
    this.emailForm.controls['recipient'].disable();
  }

  setForm(): void {
    if (this.isUpdate) {
      const activity = this.activityToUpgrade;
      const subjectWithoutDate = activity.email.subject
        .replace(/\d{2}\.\d{2}\.\d{4}$/, '')
        .trim();

      this.emailForm.setValue({
        text: activity.email.emailText,
        status: activity.status,
        subject: subjectWithoutDate, // ohne angehängtes Datum
        sender: activity.email.sender || '',
        recipient: activity.email.recipient || '',
        participantId:
          activity.email.participantId ||
          this.emailForm.controls['participantId'].value,
        sentAt: this.dateTimeService.convertStringToDate(activity.email.sentAt),
        emailType: activity.email.emailTypeId ?? 15,
      });

      console.log('Formular nach Setzen:', this.emailForm.value); // Logge das Formular nach dem Setzen
    }
  }

  findUserByName(name: string): user | undefined {
    if (!name) return undefined;

    return this.availableUsers.find(
      (user) =>
        `${user.firstname} ${user.lastname}`.toLowerCase() ===
        name.toLowerCase()
    );
  }

  emailOptions = [
    { label: 'Eingehende', number: 15 },
    { label: 'Ausgehende', number: 16 },
  ];

  selectedEmailType: number = this.emailOptions[0].number;

  //#endregion

  //#region SAVE/UPDATE

  saveEmailDialogSubmit(): void {
    const emailTypeId = this.selectedEmailType;

    // Das Datum im deutschen Format für den Betreff
    const formattedSentAtGerman = this.convertDateToGermanFormat(
      new Date(this.emailForm.controls['sentAt'].value)
    );

    const subjectWithDate = `${this.emailForm.controls['subject'].value} ${formattedSentAtGerman}`;

    const email: Email = {
      emailText: this.emailForm.controls['text'].value,
      status: this.emailForm.controls['status'].value,
      sender: this.emailForm.controls['sender'].value,
      recipient: this.emailForm.controls['recipient'].value,
      sentAt: this.dateTimeService.convertDateTimeToString(
        this.emailForm.controls['sentAt'].value
      ),
      subject: subjectWithDate, // Betreff mit deutschem Datum, ohne Klammern
      participantId: this.emailForm.controls['participantId'].value,
      emailTypeId: emailTypeId,
    };

    if (this.isUpdate) {
      this.updateEmail(email);
    } else {
      this.saveEmail(email);
    }
  }

  saveEmail(newEmail: Email): void {
    this.isSubmitting = true;
    newEmail.creatorId = this.currentUserId;

    console.log('Zu speichernde E-Mail:', newEmail);
    this.emailService.saveEmail(newEmail).subscribe({
      next: (activity) => {
        this.closeDialog({ success: true, activityId: activity.activityId });
        this.activityService.addActivity(activity);
        this.notificationService.success(
          `Aktivität "Email" erfolgreich gespeichert`,
          activity
        );
        this.isSubmitting = false;
      },
      error: () => {
        this.notificationService.error(`Aktivität "Email" NICHT gespeichert`);
        this.isSubmitting = false;
      },
    });
  }

  updateEmail(updatedEmail: Email): void {
    this.isSubmitting = true;

    updatedEmail.emailId = this.activityToUpgrade?.email?.emailId;
    updatedEmail.updator = this.authService.getUserName();
    updatedEmail.activityId = this.activityToUpgrade?.activityId;

    this.emailService.updateEmail(updatedEmail).subscribe({
      next: (activity) => {
        this.closeDialog();
        this.activityService.updateActivity(activity);
        this.notificationService.success(
          `Aktivität "Email anlegen" erfolgreich aktualisiert`,
          activity
        );
        this.isSubmitting = false;
      },
      error: () => {
        this.notificationService.error(
          `Aktivitat "Email anlegen" NICHT aktualisiert`
        );
        this.isSubmitting = false;
      },
    });
  }

  //#endregion

  //#region Auslsen von Textfeld
  removeHtmlTags(input: string): string {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.body.textContent || '';
  }

  parseEmailText(): void {
    const text = this.emailForm.controls['text'].value;
    const cleanText = this.removeHtmlTags(text);

    const gesendetMatch = cleanText.match(/Gesendet:\s*([^\r\n]+)/i);
    const an = this.extractRecipientName(cleanText);
    const betreff = this.extractSubject(text);
    const von = this.extractSenderName(cleanText);

    const gesendet = gesendetMatch ? gesendetMatch[1].trim() : '';

    // Verarbeiten des "von:"-Feldes (Absender)
    if (von) {
      this.emailForm.patchValue({ sender: von });
    }

    // Verarbeiten des "an:"-Feldes (Empfänger)
    if (an) {
      this.emailForm.patchValue({ recipient: an });
    }

    // Setzen des Betreffs
    if (betreff) {
      this.emailForm.controls['subject'].setValue(betreff);
    }

    // Verarbeiten des "Gesendet:"-Feldes (Datum)
    if (gesendet) {
      console.log('Gesendet Wert:', gesendet);

      // Flexiblere Suche nach Datum und Uhrzeit
      const dateMatch = gesendet.match(
        /(?:Gesendet:\s*)?(\w+),\s*(\d{1,2})\.\s*([\wäöüÄÖÜß]+)\s*(\d{4})\s+(\d{1,2}):(\d{2})(?:\s*Uhr)?/iu
      );

      if (dateMatch) {
        const day = parseInt(dateMatch[2], 10);
        const monthName = dateMatch[3];
        const year = parseInt(dateMatch[4], 10);
        const hours = parseInt(dateMatch[5], 10);
        const minutes = parseInt(dateMatch[6], 10);

        const monthMap: { [key: string]: number } = {
          Januar: 0,
          Februar: 1,
          März: 2,
          April: 3,
          Mai: 4,
          Juni: 5,
          Juli: 6,
          August: 7,
          September: 8,
          Oktober: 9,
          November: 10,
          Dezember: 11,
        };
        const month = monthMap[monthName];

        if (month !== undefined) {
          const formattedDate = new Date(year, month, day, hours, minutes);
          console.log('Extrahiertes Datum:', formattedDate);
          this.emailForm.controls['sentAt'].setValue(formattedDate);
        } else {
          console.error(`Monatsname nicht erkannt: ${monthName}`);
          this.notificationService.error(
            `Monatsname nicht erkannt: ${monthName}`
          );
        }
      } else {
        console.error('Datum und Uhrzeit konnten nicht extrahiert werden.');
        this.notificationService.error(
          'Datum und Uhrzeit konnten nicht extrahiert werden.'
        );
      }
    }
  }

  // Empfängernamen
  extractRecipientName(emailText: string): string {
    const senderRegex = /an:\s*(.+?>)/i;
    const match = emailText.match(senderRegex);
    const senderFull = match ? match[1].trim() : '';
    console.log('Extrahierter kompletter Absender:', senderFull);
    return senderFull;
  }

  // Sendernamen
  extractSenderName(emailText: string): string {
    const senderRegex = /von:\s*(.+?>)/i;
    const match = emailText.match(senderRegex);
    const senderName = match ? match[1].trim() : '';
    console.log('Extrahierter Sendername:', senderName);
    return senderName;
  }

  // Betreff
  extractSubject(emailText: string): string {
    const subjectMatch = emailText.match(
      /(?:Betreff:|Subject:)[^\n\r<]*(<.*?>)?\s*([^\n\r<]+)/i
    );
    if (subjectMatch) {
      let subject = subjectMatch[2].trim();

      subject = this.decodeHtml(subject);

      console.log('Extrahierter Betreff:', subject);

      let afterSubject = emailText
        .substring(subjectMatch.index + subjectMatch[0].length + subject.length)
        .trim();

      const stopIndex = afterSubject.indexOf('&nbsp;');
      if (stopIndex !== -1) {
        afterSubject = afterSubject.substring(0, stopIndex).trim();
        console.log('Text bis &nbsp;:', afterSubject);
      }
      return subject;
    }
    return 'Kein Betreff gefunden';
  }

  decodeHtml(html: string): string {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  // Datum Conversion
  convertStringToDate(dateString: string): Date {
    const parsedDate = Date.parse(dateString);
    if (!isNaN(parsedDate)) {
      return new Date(parsedDate);
    }
    return new Date();
  }

  // DAtum conversion zu Deutsches Datumformat
  convertDateToGermanFormat(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };

    // Rückgabe des Datums im deutschen Format (z.B. 13.05.2025)
    return date.toLocaleDateString('de-DE', options);
  }
}

//#endregion
